import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { EventsService } from "../../services/events/events.service";
import { GlobalDataService } from "../../services/faker/global-data.service";
import { selectLoggedInGuardianLearnerIds } from "../guardian/guardian.selectors";
import { EventActions } from "./event.actions";
import { selectAllActiveStudentIds } from "../students/student.selectors";

@Injectable()
export class EventEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly eventsService: EventsService,
        private readonly globalDataService: GlobalDataService,
        private readonly store: Store
    ) { }

    loadEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EventActions.loadEvents),
            withLatestFrom(this.store.select(selectAllActiveStudentIds)),
            mergeMap(([_, studentIds]) => {
                if (!studentIds.length) {
                    return of(EventActions.loadEventsFailure({
                        error: 'No student IDs found for logged in guardian'
                    }));
                }

                // Set year range: current year - 1 to current year + 1
                const currentDate = new Date();
                const currentYear = currentDate.getFullYear();
                const startYear = new Date(currentYear - 1, 0, 1); // Jan 1 of last year
                const endYear = new Date(currentYear + 1, 11, 31); // Dec 31 of next year

                return this.eventsService.getEventsForStudents(studentIds, startYear, endYear).pipe(
                    map(events => {
                        if (events && events.length > 0) {
                            return EventActions.loadEventsSuccess({ events });
                        } else {
                            return EventActions.loadEventsFailure({ error: 'No events found' });
                        }
                    }),
                    catchError(error => {
                        console.error('Error loading events:', error);
                        return of(EventActions.loadEventsFailure({
                            error: error instanceof Error ? error.message : 'Unknown error occurred'
                        }));
                    })
                );
            })
        )
    );

    loadFakeEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EventActions.loadFakeEvents),
            switchMap(() => {
                const data = this.globalDataService.getEvents();
                if (data && data.length > 0) {
                    return of(data).pipe(
                        map(events => EventActions.loadFakeEventsSuccess({ events })),
                        catchError(error => of(EventActions.loadFakeEventsFailure({ error })))
                    );
                } else {
                    return of(EventActions.loadFakeEventsFailure({ error: 'No fake event data available' }));
                }
            })
        )
    );
}
